import React, { useEffect, useState } from 'react';
import KeywordHighlighter from './KeywordHighlighter';
import { X, FlaskConical, ChevronDown, ChevronRight, LayoutGrid, List, GripVertical } from 'lucide-react';

// ── Formatting helpers ───────────────────────────────────────────────────────

const formatKey = (key) => {
    if (!key) return '';
    return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
        .replace(/\s+/g, ' ');
};

// ── Canonical section order & display labels (unified schema) ────────────────
// Sections not in this map will be rendered at the end in the order they appear.
const SECTION_ORDER = [
    ['generalInfo',           'General Information'],
    ['specimenCollection',    'Specimen & Collection'],
    ['handlingAndProcessing', 'Handling & Processing'],
    ['availability',          'Availability'],
    ['billingInfo',           'Billing Information'],
    ['labUseOnly',            'Laboratory Use Only'],
    ['geneticInfo',           'Genetic Information'],
    ['panelInfo',             'Panel Information'],
    ['relatedDocuments',      'Related Documents'],
];

// ── Priority field order per section ─────────────────────────────────────────
// Fields listed here always appear at the top of their section in this order.
// Any field NOT in the list falls to the bottom, sorted alphabetically.
const FIELD_PRIORITY_ORDER = {
    generalInfo: [
        'Test Name', 'Test Code', 'Test ID', 'Also Known As', 'Alternative Names',
        'Alternate Names', 'Synonyms', 'Laboratory', 'Department', 'Assay Frequency',
        'Turnaround Time', 'Description', 'Clinical Significance', 'Frequency',
    ],
    specimenCollection: [
        'Specimen Type', 'Specimen Types', 'Specimen types', 'Container', 'Container Type',
        'Container Types', 'Volume', 'Adults Volume or Mass',
        'Minimum/Paediatric Volume or Mass', 'Collection & Request Instructions',
        'Transport Instructions between Sites and/or Laboratories',
        'External Transport Instructions', 'Special Instructions', 'Stability',
    ],
    handlingAndProcessing: [
        'Storage', 'Temperature', 'Stability', 'Processing', 'Special Requirements',
        'Instructions',
    ],
    labUseOnly: [
        'External Laboratory', 'Sending Lab Details', 'Notes', 'Comments',
        'Referred Laboratory', 'Reference Laboratory',
    ],
    billingInfo: [
        'Medicare Item Number', 'MBS Item', 'Schedule Fee', 'Benefit', 'Fee',
    ],
    geneticInfo: [
        'Genes Tested', 'Gene', 'Panels Tested', 'Transcript', 'OMIM',
    ],
};

/**
 * Sort the entries of a section object so that known priority fields appear
 * first (in order), then unknown fields appear at the bottom alphabetically.
 */
const sortSectionEntries = (sectionKey, data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
    const priorityList = FIELD_PRIORITY_ORDER[sectionKey] || [];
    const entries = Object.entries(data);
    const priorityMap = new Map(priorityList.map((k, i) => [k, i]));
    entries.sort(([a], [b]) => {
        const ai = priorityMap.has(a) ? priorityMap.get(a) : Infinity;
        const bi = priorityMap.has(b) ? priorityMap.get(b) : Infinity;
        if (ai !== bi) return ai - bi;
        // Both unknown → alphabetical
        return a.localeCompare(b);
    });
    return Object.fromEntries(entries);
};


// ── Value renderer ───────────────────────────────────────────────────────────

const RecursiveValueRenderer = ({ data, searchTerm, enableSampleHighlighting }) => {
    if (data === null || data === undefined || data === '')
        return <span className="text-[13px]" style={{ color: 'var(--text-faint)' }}>N/A</span>;

    // Document-link arrays: [{label, href}, ...]
    if (Array.isArray(data)) {
        if (data.every(item => typeof item === 'object' && item !== null && item.href && item.label)) {
            return (
                <ul className="list-disc list-inside space-y-0.5 ml-1">
                    {data.map((doc, index) => (
                        <li key={index} className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                            <a
                                href={doc.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                referrerPolicy="no-referrer"
                                className="text-indigo-300/80 hover:text-indigo-300 underline underline-offset-4 decoration-indigo-500/20 hover:decoration-indigo-500/50 transition-colors duration-150"
                            >
                                {doc.label}
                            </a>
                        </li>
                    ))}
                </ul>
            );
        }
        return (
            <ul className="list-disc list-inside space-y-0.5 ml-1">
                {data.map((item, index) => (
                    <li key={index} className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                        <RecursiveValueRenderer data={item} searchTerm={searchTerm} enableSampleHighlighting={enableSampleHighlighting} />
                    </li>
                ))}
            </ul>
        );
    }

    // Object with image thumbnail
    if (typeof data === 'object' && data.text && data.imageUrl) {
        return (
            <div className="flex items-center gap-3">
                <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>
                    <KeywordHighlighter text={data.text} searchTerm={searchTerm} enableSampleHighlighting={enableSampleHighlighting} />
                </span>
                <img src={data.imageUrl} alt={`Container: ${data.text}`} className="w-10 h-10 object-cover rounded border" style={{ borderColor: 'var(--border-secondary)' }} />
            </div>
        );
    }

    // Nested object — render recursively
    if (typeof data === 'object') {
        return (
            <div className="flex flex-col gap-1">
                {Object.entries(data).map(([key, value]) => (
                    <div key={key} className="pl-2.5 border-l-2 border-indigo-500/20">
                        <strong className="text-[11px] block" style={{ color: 'var(--text-muted)' }}>{formatKey(key)}</strong>
                        <RecursiveValueRenderer data={value} searchTerm={searchTerm} enableSampleHighlighting={enableSampleHighlighting} />
                    </div>
                ))}
            </div>
        );
    }

    // String — check for images, HTML, or plain text
    if (typeof data === 'string') {
        const potentialUrls = data.split(',').map(s => s.trim());
        if (potentialUrls.every(url => url.match(/\.(jpeg|jpg|gif|png)$/i))) {
            return (
                <div className="flex flex-wrap gap-1.5">
                    {potentialUrls.map((url, index) => (
                        <img key={index} src={url} alt={`Specimen tube ${index + 1}`} className="w-10 h-10 object-cover rounded border" style={{ borderColor: 'var(--border-secondary)' }} />
                    ))}
                </div>
            );
        }
        if (/<[a-z][\s\S]*>/i.test(data)) {
            return (
                <div
                    className="text-[13px] leading-snug prose prose-invert prose-sm max-w-none prose-a:text-indigo-300/80 prose-a:underline prose-a:underline-offset-4 prose-a:decoration-indigo-500/20 hover:prose-a:text-indigo-300"
                    style={{ color: 'var(--text-primary)' }}
                    dangerouslySetInnerHTML={{ __html: data }}
                />
            );
        }
        return (
            <span className="text-[13px] leading-snug break-words" style={{ color: 'var(--text-primary)' }}>
                <KeywordHighlighter text={String(data)} searchTerm={searchTerm || ''} enableSampleHighlighting={enableSampleHighlighting} />
            </span>
        );
    }

    return <span className="text-[13px]" style={{ color: 'var(--text-primary)' }}>{String(data)}</span>;
};

// ── Collapsible Detail Section (Accordion) ───────────────────────────────────

const DetailSection = ({ title, data, searchTerm, enableSampleHighlighting, defaultOpen = true }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    // Guard: skip empty sections
    if (!data) return null;
    if (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0) return null;
    if (Array.isArray(data) && data.length === 0) return null;

    const isDocList = Array.isArray(data) && data.every(item => item && item.href && item.label);

    return (
        <div className="mb-2 last:mb-0 rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-primary)' }}>
            {/* Accordion Header */}
            <button
                onClick={() => setIsOpen(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors duration-100 hover:bg-white/[0.03]"
                style={{ backgroundColor: 'var(--bg-elevated)' }}
                aria-expanded={isOpen}
            >
                <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'var(--text-faint)' }}>
                    {title}
                </span>
                <span style={{ color: 'var(--text-faint)', flexShrink: 0 }}>
                    {isOpen
                        ? <ChevronDown size={13} />
                        : <ChevronRight size={13} />
                    }
                </span>
            </button>

            {/* Accordion Body — CSS max-height transition */}
            <div
                style={{
                    maxHeight: isOpen ? '2000px' : '0px',
                    overflow: 'hidden',
                    transition: 'max-height 0.22s ease-in-out',
                }}
            >
                <div className="px-4 py-3">
                    {isDocList ? (
                        <RecursiveValueRenderer data={data} searchTerm={searchTerm} enableSampleHighlighting={enableSampleHighlighting} />
                    ) : (
                        <table className="w-auto">
                            <tbody>
                                {Object.entries(data).map(([key, value]) => {
                                    if (value === null || value === undefined || value === '') return null;

                                    const isEmphasized = key === 'External Laboratory';

                                    return (
                                        <tr
                                            key={key}
                                            className={isEmphasized ? 'bg-indigo-500/[0.12]' : ''}
                                            style={isEmphasized ? { borderLeft: '4px solid #818cf8' } : {}}
                                        >
                                            <td
                                                className="text-[13px] pr-8 py-2.5 align-top whitespace-nowrap"
                                                style={{
                                                    color: isEmphasized ? '#a5b4fc' : 'var(--text-muted)',
                                                    fontWeight: isEmphasized ? '700' : 'normal',
                                                    borderBottom: isEmphasized
                                                        ? '1px solid rgba(129, 140, 248, 0.3)'
                                                        : '1px solid color-mix(in srgb, var(--border-primary) 40%, transparent)',
                                                    paddingLeft: isEmphasized ? '14px' : '0',
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {isEmphasized && <FlaskConical size={14} className="text-indigo-400" />}
                                                    {formatKey(key)}
                                                </div>
                                            </td>
                                            <td
                                                className="py-2.5 align-top"
                                                style={{
                                                    borderBottom: isEmphasized
                                                        ? '1px solid rgba(129, 140, 248, 0.3)'
                                                        : '1px solid color-mix(in srgb, var(--border-primary) 40%, transparent)',
                                                    color: isEmphasized ? '#e0e7ff' : 'inherit',
                                                }}
                                            >
                                                <div className={isEmphasized ? 'font-medium' : ''}>
                                                    <RecursiveValueRenderer data={value} searchTerm={searchTerm} enableSampleHighlighting={enableSampleHighlighting} />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Main DetailsModal ─────────────────────────────────────────────────────────

export const DetailsModal = ({ result, onClose, searchTerm, enableSampleHighlighting }) => {
    // Layout state: 'single' | 'grid'
    const [layout, setLayout] = useState('single');

    // Keyboard close
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    if (!result) return null;

    // Unified key: all parsers now emit 'details'
    const details = result.details || result.inline_details || {};
    const title = details.Title || result.title || 'Details';
    const sourceLink = result.link && result.link !== '#' ? result.link : null;

    // Build ordered sections list — apply priority sort to each section's fields
    const renderedSectionKeys = new Set(SECTION_ORDER.map(([k]) => k));
    const orderedSections = [];

    // 1. Walk canonical order first
    for (const [key, label] of SECTION_ORDER) {
        if (details[key] !== undefined && details[key] !== null) {
            orderedSections.push({ key, label, data: sortSectionEntries(key, details[key]) });
        }
    }

    // 2. Append any extra (unrecognised) sections at the end
    for (const [key, value] of Object.entries(details)) {
        if (key === 'Title' || renderedSectionKeys.has(key)) continue;
        orderedSections.push({ key, label: formatKey(key), data: sortSectionEntries(key, value) });
    }

    // Fallback: if no sections at all, dump the whole details as a flat section
    const hasSections = orderedSections.length > 0;
    const fallbackData = hasSections
        ? null
        : Object.fromEntries(Object.entries(details).filter(([k]) => k !== 'Title'));

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            {/*
              * Resizable modal box.
              * resize: both + min/max constraints give the user a native drag handle (bottom-right corner).
              * overflow: auto is required for `resize` to function.
              */}
            <div
                id="details-modal-box"
                className="relative rounded-2xl shadow-2xl shadow-black/60 custom-scrollbar"
                style={{
                    backgroundColor: 'var(--bg-surface)',
                    border: '1px solid var(--border-secondary)',
                    width: '90vw',
                    maxWidth: '1280px',
                    minWidth: '360px',
                    height: '85vh',
                    minHeight: '300px',
                    maxHeight: '95vh',
                    resize: 'both',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* ── Modal Header ─────────────────────────────────────── */}
                <div
                    className="sticky top-0 z-10 flex items-start justify-between px-7 pt-5 pb-3"
                    style={{ backgroundColor: 'var(--bg-surface)', borderBottom: '1px solid var(--border-primary)' }}
                >
                    <div className="flex-1 pr-4">
                        <h2
                            className="text-base font-semibold tracking-tight leading-tight"
                            style={{ color: 'var(--text-primary)' }}
                        >
                            {title}
                        </h2>
                        {sourceLink && (
                            <a
                                href={sourceLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                referrerPolicy="no-referrer"
                                className="text-[11px] text-indigo-400/70 hover:text-indigo-400 transition-colors underline underline-offset-2 mt-0.5 inline-block"
                            >
                                View source →
                            </a>
                        )}
                    </div>

                    {/* Header controls */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {/* Layout toggle */}
                        <button
                            id="details-modal-layout-toggle"
                            title={layout === 'single' ? 'Switch to two-column layout' : 'Switch to single-column layout'}
                            onClick={() => setLayout(l => l === 'single' ? 'grid' : 'single')}
                            className="p-1.5 rounded-lg transition-all duration-150 hover:bg-white/[0.06]"
                            style={{ color: layout === 'grid' ? 'var(--text-primary)' : 'var(--text-faint)' }}
                        >
                            {layout === 'single' ? <LayoutGrid size={15} /> : <List size={15} />}
                        </button>

                        {/* Close */}
                        <button
                            id="details-modal-close"
                            className="p-1.5 rounded-lg transition-all duration-150 hover:bg-white/[0.06]"
                            style={{ color: 'var(--text-faint)' }}
                            onClick={onClose}
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* ── Modal Body ───────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto px-7 py-5 custom-scrollbar">
                    {hasSections ? (
                        <div
                            style={{
                                display: layout === 'grid' ? 'grid' : 'flex',
                                gridTemplateColumns: layout === 'grid' ? 'repeat(2, 1fr)' : undefined,
                                flexDirection: layout === 'grid' ? undefined : 'column',
                                gap: '8px',
                                alignItems: 'start',
                            }}
                        >
                            {orderedSections.map(({ key, label, data }) => (
                                <DetailSection
                                    key={key}
                                    title={label}
                                    data={data}
                                    searchTerm={searchTerm}
                                    enableSampleHighlighting={enableSampleHighlighting}
                                />
                            ))}
                        </div>
                    ) : (
                        <DetailSection
                            title="Details"
                            data={fallbackData}
                            searchTerm={searchTerm}
                            enableSampleHighlighting={enableSampleHighlighting}
                        />
                    )}
                </div>

                {/* Resize affordance hint */}
                <div
                    className="absolute bottom-1 right-1 flex items-center pointer-events-none"
                    style={{ color: 'var(--text-faint)', opacity: 0.35 }}
                    title="Drag corner to resize"
                >
                    <GripVertical size={14} />
                </div>
            </div>
        </div>
    );
};